import { createRootRoute, createRoute, lazyRouteComponent } from '@tanstack/react-router';
import React from 'react';

import { Routes } from 'shared/routes';

import { AuthLayout, RootLayout, PublicLayout } from './layouts';

// Use lazyRouteComponent for all page-level routes with relative paths
const HomePage = lazyRouteComponent(() => import('../pages/home'));
const NotFoundPage = lazyRouteComponent(() => import('../pages/not-found'));
const LoginPage = lazyRouteComponent(() => import('../pages/login'));
const RegisterPage = lazyRouteComponent(() => import('../pages/register'));
const ConfirmPage = lazyRouteComponent(() => import('../pages/confirm'));
const AppPage = lazyRouteComponent(() => import('../pages/app'));

// Rules pages
const RulesPage = lazyRouteComponent(() => import('../pages/rules'));
const RuleCreatePage = lazyRouteComponent(() => import('../pages/rule-create'));
const RuleEditPage = lazyRouteComponent(() => import('../pages/rule-edit'));
const CommunityRulesPage = lazyRouteComponent(() => import('../pages/community-rules'));

// MCPs pages
const MCPsPage = lazyRouteComponent(() => import('../pages/mcps'));
const MCPViewPage = lazyRouteComponent(() => import('../pages/mcp-view'));
const MCPCreatePage = lazyRouteComponent(() => import('../pages/mcp-create'));
const MCPEditPage = lazyRouteComponent(() => import('../pages/mcp-edit'));
const MCPRulesPage = lazyRouteComponent(() => import('../pages/mcp-rules'));

// Define the root route
export const RootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

export const PublicLayoutRoute = createRoute({
  id: 'publicLayout',
  getParentRoute: () => RootRoute,
  component: PublicLayout,
});

export const AuthLayoutRoute = createRoute({
  id: 'authLayout',
  getParentRoute: () => RootRoute,
  component: AuthLayout,
});

export const LoginRoute = createRoute({
  getParentRoute: () => PublicLayoutRoute,
  path: Routes.frontend.loginPath,
  component: LoginPage,
});

export const RegisterRoute = createRoute({
  getParentRoute: () => PublicLayoutRoute,
  path: Routes.frontend.registerPath,
  component: RegisterPage,
});

export const ConfirmRoute = createRoute({
  getParentRoute: () => PublicLayoutRoute,
  path: Routes.frontend.confirmPath,
  component: ConfirmPage,
});

export const HomeRoute = createRoute({
  getParentRoute: () => PublicLayoutRoute,
  path: Routes.frontend.rootPath,
  component: HomePage,
});

export const AppRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.appPath,
  component: AppPage,
});

// Rules routes
export const RulesRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.rulesPath,
  component: RulesPage,
});

export const RuleCreateRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.ruleCreatePath,
  component: RuleCreatePage,
});

export const RuleEditRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: '/rules/$ruleId/edit',
  component: () => {
    const { ruleId } = RuleEditRoute.useParams();
    return React.createElement(RuleEditPage, { ruleId });
  },
});

export const CommunityRulesRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.communityRulesPath,
  component: CommunityRulesPage,
});

// MCPs routes
export const MCPsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.mcpsPath,
  component: MCPsPage,
});

export const MCPViewRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: '/mcps/$mcpId/view',
  component: () => {
    const { mcpId } = MCPViewRoute.useParams();
    return React.createElement(MCPViewPage, { mcpId });
  },
});

export const MCPCreateRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: Routes.frontend.mcpCreatePath,
  component: MCPCreatePage,
});

export const MCPEditRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: '/mcps/$mcpId/edit',
  component: () => {
    const { mcpId } = MCPEditRoute.useParams();
    return React.createElement(MCPEditPage, { mcpId });
  },
});

export const MCPRulesRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: '/mcps/$mcpId/rules',
  component: () => {
    const { mcpId } = MCPRulesRoute.useParams();
    return React.createElement(MCPRulesPage, { mcpId });
  },
});

export const routeTree = RootRoute.addChildren([
  AuthLayoutRoute.addChildren([
    AppRoute,
    RulesRoute,
    RuleCreateRoute,
    RuleEditRoute,
    CommunityRulesRoute,
    MCPsRoute,
    MCPViewRoute,
    MCPCreateRoute,
    MCPEditRoute,
    MCPRulesRoute,
  ]),
  PublicLayoutRoute.addChildren([LoginRoute, RegisterRoute, ConfirmRoute, HomeRoute]),
]);
